import React, { useEffect, useMemo, useState } from 'react';

import NoticesPanel from './NoticesPanel';
import { sortNotices } from './noticeKinds';
import useDismissedNotices from './useDismissedNotices';
import useSelectedResource from '../../hooks/useSelectedResource';
import { axiosInstance } from '../../routes/axiosInstance';
import { RESOURCE_TIMEZONE } from '../../utils/datetime';
import { buildDstNotice } from '../../utils/dstNotice';

/**
 * Notices above the dashboard hero: superuser-posted ones for the user's role
 * and selected resource (/market/notices/active), plus the automatic
 * clock-change notice from the resource's timezone. Dismissals live in this
 * browser; the Notices page lists dismissed notices and can bring them back.
 */
export default function DashboardNotices({ className = '' }) {
  const resource = useSelectedResource();
  const timezone = resource?.timezone || RESOURCE_TIMEZONE;
  const [posted, setPosted] = useState([]);
  const { isDismissed, dismiss } = useDismissedNotices();

  useEffect(() => {
    let stale = false;
    axiosInstance.get('/market/notices/active', { params: resource?.id ? { resource: resource.id } : {} })
      .then((response) => {
        if (!stale) setPosted(response.data.data || []);
      })
      // Notices are a courtesy: a failed fetch leaves the dashboard as is.
      .catch(() => {
        if (!stale) setPosted([]);
      });
    return () => {
      stale = true;
    };
  }, [resource?.id]);

  const dst = useMemo(() => buildDstNotice(timezone), [timezone]);

  const notices = useMemo(
    () => sortNotices([...posted, ...(dst ? [dst] : [])]).filter((notice) => !isDismissed(notice)),
    [posted, dst, isDismissed]
  );

  return <NoticesPanel notices={notices} onDismiss={dismiss} allNoticesLink className={className} />;
}
